import m from 'mithril';
import { asGrouped } from './format.ts';

interface NumberInputAttrs {
    value: number;
    onchange: (value: number) => any;
    step?: number;
    id: string;
    text?: string;
    prefix?: string;
    afterText?: string;
    group?: boolean;
    chars?: number;
}

/**
 * A sentence with the number set into it, rather than a label on one side of
 * the panel and a control on the other. `prefix` is the unit that reads before
 * the number (a currency symbol), `afterText` the one that reads after it, and
 * the whole line wraps like the prose it imitates.
 *
 * `chars` is how wide the blank is, in characters — nothing sizes a text input
 * to its content, so each field says how big its numbers get.
 *
 * `group` separates thousands, for the fields where a bare 250000 is hard to
 * read. It costs `type=number`, which rejects a value with commas in it, so a
 * grouped field is a text input that parses the commas back off, and steps on
 * the arrow keys itself since a text input has no stepper of its own.
 */
export const NumberInput: m.Component<NumberInputAttrs, {}> = {
    view: ({
        attrs: {
            value,
            onchange,
            step = 1,
            id,
            text = '',
            prefix = '',
            afterText = '',
            group = false,
            chars = 8,
        },
    }) =>
        m('.field', [
            m('label.field__text', { for: id }, text),
            prefix ? m('span.field__affix', prefix) : null,
            m('input.field__input', {
                id,
                // A cleared field parses to NaN either way, which the models
                // already have to handle.
                value: group ? asGrouped(value) : value,
                type: group ? 'text' : 'number',
                inputmode: group ? 'numeric' : 'decimal',
                step: group ? undefined : step,
                style: { width: `${chars}ch` },
                onchange: (e) => {
                    const raw = group
                        ? e.target.value.replace(/,/g, '')
                        : e.target.value;
                    onchange(parseFloat(raw));
                },
                onkeydown: group
                    ? (e) => {
                          const dir =
                              e.key === 'ArrowUp'
                                  ? 1
                                  : e.key === 'ArrowDown'
                                    ? -1
                                    : 0;
                          if (!dir) {
                              // Every other key is the user typing. Redrawing
                              // here would rewrite the field from the model,
                              // which onchange has not updated yet.
                              e.redraw = false;
                              return;
                          }
                          e.preventDefault();
                          const current = parseFloat(
                              e.target.value.replace(/,/g, ''),
                          );
                          const next =
                              (isNaN(current) ? 0 : current) + dir * step;
                          e.target.value = asGrouped(next);
                          onchange(next);
                      }
                    : undefined,
            }),
            afterText ? m('span.field__affix', afterText) : null,
        ]),
};
